/**
 * Horizontal Scroller — vertical scroll drives a horizontal run.
 *
 * Pins `[data-hscroll]` for as long as it takes to translate
 * `[data-hscroll-track]` from its left edge to its right, so scrolling down
 * moves the content sideways. Reference: unitedcarriers.com.
 *
 * ── ONE MODULE, WRITTEN FOR TWO BLOCKS ──────────────────────────────────────
 *
 * Toby De Lorenzo wrote this on the `designs` branch for a case-study row and
 * generalised it when the career timeline wanted the same behaviour. On main
 * only the timeline layout of feature_columns uses it — the case-studies block
 * took a scroll-snap slider instead — but the split stands: the pin, the
 * distance maths and the refresh handling are the general part, and what rides
 * along is `buildSteppedRail()` below, which only a section carrying nodes and
 * a marker gets. A block that wants a plain continuous slide needs nothing but
 * the three `data-hscroll*` hooks.
 *
 * ── THE STACKED GRID IS THE BASE STATE, NOT A FALLBACK ──────────────────────
 *
 * Everything here is an enhancement over a block that already works. The CSS
 * that makes the row horizontal is inside `@media (min-width: 64rem)`, and this
 * module bails on the same three conditions the CSS does — narrow viewport,
 * reduced motion, no GSAP. So a phone, a reduced-motion visitor and a visitor
 * whose deferred bundle never arrived all get the vertical stack, which is the
 * unmodified block. There is no separate mobile implementation to keep in step.
 *
 * ── WHY THE DISTANCE IS A FUNCTION, NOT A NUMBER ────────────────────────────
 *
 * `end` and the tween distance are both read at refresh time via callbacks, and
 * `invalidateOnRefresh` forces the tween to re-read its own start/end. Measured
 * once at init, the numbers are wrong the moment anything changes width: a
 * resize, the H&Co fonts landing and reflowing every card title, or an image
 * finally decoding. The symptom is not subtle — the track stops short of the
 * last card, or runs past it into empty space, and it only reproduces on a slow
 * connection.
 *
 * ── SWUP ────────────────────────────────────────────────────────────────────
 *
 * app.js already kills every ScrollTrigger before a content swap and refreshes
 * after, so nothing here needs its own teardown; it just has to be re-run on
 * `page:view`, alongside the other reveal initialisers.
 */

/** Matches the CSS breakpoint that turns the row horizontal — keep in step. */
const DESKTOP_QUERY = '(min-width: 64rem)';

/** Marks a section as wired so re-entry is idempotent (Swup calls this again). */
const STATE_ATTR = 'data-hscroll-state';

/**
 * Builds the stepped rhythm a node rail needs: the track, the travelling marker
 * and the node fills all advance together, segment by segment, with a dwell at
 * each stop.
 *
 * ── WHY THE TRACK IS NOT ONE LINEAR TWEEN HERE ──────────────────────────────
 *
 * A row of cards (the case studies) wants one continuous slide, and gets it. A
 * rail with stops wants to ARRIVE — to reach a dot, hold there while the reader
 * keeps scrolling, then move on. That hold cannot be faked by pausing the marker
 * alone: the track would keep sliding underneath it and the marker would appear
 * to drift backwards off the dot it had just reached. Both have to step.
 *
 * Each segment is one timeline unit: MOVE for the first three quarters, HOLD for
 * the last. The hold is what makes the marker read as hooking onto the dot
 * rather than passing over it.
 *
 * ── THE FILLS ARE A CUSTOM PROPERTY, NOT AN ELEMENT ─────────────────────────
 *
 * Upcoming dots are drawn as an empty ring and fill from the centre as the marker
 * arrives. The fill is a pseudo-element scaled by `--node-fill`, because a
 * pseudo-element cannot be handed to GSAP directly — animating the property on
 * the node itself is what reaches it.
 *
 * @param {Element} section
 * @param {object} timeline the section's scrubbed GSAP timeline
 * @param {Element} track
 * @param {Function} distance
 * @returns {void}
 */
function buildSteppedRail(section, timeline, track, distance) {
    const marker = section.querySelector('[data-hscroll-marker]');
    const nodes = [...section.querySelectorAll('[data-hscroll-node]')];
    const ring = marker.firstElementChild ?? marker;
    const segments = nodes.length - 1;

    /** Fraction of each segment spent moving; the rest is the dwell. */
    const MOVE = 0.75;

    /*
     * Offsets are read from the nodes themselves rather than computed from a
     * column width, so a change to the spacing cannot put the marker between two
     * dots.
     */
    const railStart = nodes[0].offsetLeft;
    const stops = nodes.map((node) => node.offsetLeft - railStart);

    // The first dot is already reached, so it starts filled.
    window.gsap.set(nodes[0], { '--node-fill': 1 });
    window.gsap.set(nodes.slice(1), { '--node-fill': 0 });

    for (let i = 1; i <= segments; i += 1) {
        const at = i - 1;

        // The track and the marker move together, then both hold.
        timeline.to(track, {
            x: () => -distance() * (i / segments),
            ease: 'none',
            duration: MOVE,
        }, at);

        timeline.to(marker, {
            x: stops[i],
            ease: 'power1.inOut',
            duration: MOVE,
        }, at);

        // Arrival: the dot fills from its centre and the marker's ring swells.
        timeline.to(nodes[i], {
            '--node-fill': 1,
            duration: 0.18,
            ease: 'power2.out',
        }, at + MOVE);

        timeline.to(ring, {
            scale: 1.15,
            duration: 0.09,
            yoyo: true,
            repeat: 1,
            ease: 'power2.out',
        }, at + MOVE);
    }
}

export function initHorizontalScrollers() {
    if (!window.gsap || !window.ScrollTrigger) {
        return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;

    if (reducedMotion || !isDesktop) {
        return;
    }

    document.querySelectorAll('[data-hscroll]').forEach((section) => {
        if (section.hasAttribute(STATE_ATTR)) {
            return;
        }

        const track = section.querySelector('[data-hscroll-track]');
        const viewport = section.querySelector('[data-hscroll-viewport]');

        if (!track || !viewport) {
            return;
        }

        /**
         * How far the track has to travel: its full width less the window it is
         * seen through. Read from the VIEWPORT rather than the track's own
         * clientWidth — the track is `width: max-content`, so its client width
         * is its full width and the distance would come out as zero.
         */
        const distance = () => Math.max(0, track.scrollWidth - viewport.clientWidth);

        // A row that already fits has nothing to scroll, and pinning it would
        // hold the reader still for a screen's worth of nothing.
        if (distance() <= 0) {
            return;
        }

        section.setAttribute(STATE_ATTR, 'pinned');

        const nodes = section.querySelectorAll('[data-hscroll-node]');
        const hasRail = section.querySelector('[data-hscroll-marker]') && nodes.length >= 2;
        const segments = hasRail ? nodes.length - 1 : 1;

        /**
         * How much SCROLL the run is given, which is not the same as how far the
         * track travels.
         *
         * A plain row spends exactly its own width: scroll one pixel, move one
         * pixel, which is what makes it feel directly driven.
         *
         * A stepped rail cannot. Its segments spend a quarter of themselves
         * standing still at a dot, and with the range set to the travel distance
         * that dwell came out at ~74px of scrolling on a 1440 screen — less than
         * one notch of a wheel, so the hold that is supposed to read as arriving
         * was invisible. Each segment gets half a viewport of scroll on top, so
         * the dwell is something a reader can actually feel.
         */
        const scrollLength = () => (hasRail
            ? distance() + segments * window.innerHeight * 0.5
            : distance());

        /* Per-block extras ride the SAME timeline rather than making their own
           ScrollTrigger. Two triggers pinning and scrubbing the same section
           would each compute their own start/end and drift apart on refresh; one
           timeline means the marker below cannot get out of step with the track
           it is measuring against. */
        const timeline = window.gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top top',
                end: () => `+=${scrollLength()}`,
                pin: true,
                // A little scrub smooths the tie to the scroll position without
                // the track feeling detached from the wheel.
                scrub: 0.6,
                invalidateOnRefresh: true,

                /* Softens the one-frame jump when the pin engages at speed.

                   IT WAS BRIEFLY REMOVED ON BAD EVIDENCE, and the reason is
                   worth keeping. Sampling the run with `window.scrollTo` showed
                   the section 191px and 289px above the viewport early on, which
                   looked like a mispredicted pin. It was the measurement: a
                   teleport gives ScrollTrigger no real scroll events to settle
                   a fixed-position pin against, so the first samples after each
                   jump read a transient. Driving the same run with the wheel in
                   90px steps holds secTop at 0 and the heading at 140px for
                   every sample of the pinned range. Measure pinning with the
                   wheel, never with scrollTo. */
                anticipatePin: 1,
            },
        });

        /*
         * THE TRACK TWEEN SPANS THE WHOLE TIMELINE, and its duration has to be
         * computed rather than left at GSAP's default of 1.
         *
         * `decorate()` adds one unit-length segment per gap between nodes, so a
         * three-node rail makes a timeline two units long. With the track left at
         * duration 1 it finished in the first half and then sat still while the
         * marker walked the rest — measured on the career timeline: the track was
         * fully translated by roughly 30% of the scroll, and the remaining 70%
         * moved nothing but the dot.
         */
        if (hasRail) {
            buildSteppedRail(section, timeline, track, distance);
        } else {
            // A plain row: one continuous slide, nothing to arrive at.
            timeline.to(track, { x: () => -distance(), ease: 'none' }, 0);
        }
    });
}
